import Service from 'react-reservice';

import { ESymbolType } from '@webtrader/core/api/market/symbols';
import symbolDetailAPI, { ISymbolDetail } from '@webtrader/core/api/market/symbols/detail';
import watchlistAPI from '@webtrader/core/api/market/watchlist';
import signalsAPI, { ISignal } from '@webtrader/core/api/plugin/signals';
import signalsDetailsAPI, { ISignalDetails } from '@webtrader/core/api/plugin/signals/details';
import { ELoginSource } from '@webtrader/core/api/user/types';
import { ESignalFilter, SIGNAL_CATEGORY_FILTERS } from '@webtrader/core/constants/signal';
import i18n from '@webtrader/core/i18n';
import { IApiError } from '@webtrader/core/request/api';
import { mapResponseCodeToMessage } from '@webtrader/core/request/message';
import { authService } from '@webtrader/core/services/auth';
import { chartService } from '@webtrader/core/services/chart';
import { configService } from '@webtrader/core/services/config';
import { EAppType } from '@webtrader/core/services/config/service';
import { newOrderService } from '@webtrader/core/services/form/order/new';
import { orderService } from '@webtrader/core/services/order';

import { userPreferenceService } from '../userPreference';

interface ISignalsContext {
  loading: boolean;
  loadingSignalDetails: boolean;
  loadingSymbolDetails: boolean;
  loadingSignalCategories: boolean;
  savingSignalCategories: boolean;
  keyword: string;
  signals: ISignal[];
  favourites: ISignal[];
  selectedCategories: ESymbolType[];
  originalSelectedCategories: ESymbolType[];
  selectedFilter?: ESignalFilter;
  selectedSignal?: ISignal;
  signalToView?: AtLeast<ISignal, 'id' | 'symbol'>;
  selectedSignalDetails?: ISignalDetails;
  selectedSymbolDetails?: ISymbolDetail;
}

interface ISetSignalToViewWithPropsParams {
  id: ISignal['id'];
  symbol: ISignal['symbol'];
}

interface IOnSelectSignalFromNotificationParams {
  symbol: string;
  signalId: string;
}

interface IFetchSignalsParams {
  skipSelectDefault?: boolean;
  loading?: boolean;
}

const defaultContext: ISignalsContext = {
  loading: true,
  loadingSignalDetails: true,
  loadingSymbolDetails: true,
  loadingSignalCategories: true,
  savingSignalCategories: false,
  keyword: '',
  signals: [],
  favourites: [],
  selectedCategories: [],
  originalSelectedCategories: [],
  selectedFilter: ESignalFilter.FAVOURITE,
  signalToView: undefined
};

class SignalsService extends Service<ISignalsContext> {
  private initialised = false;

  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public init = async () => {
    const appType = configService.getAppType();

    if (appType === EAppType.PC) {
      this.getSignalForPC();
    } else {
      this.getSignalForMobile();
    }
  };

  private getSignalForPC = async () => {
    if (!this.initialised) {
      this.fetchSelectedSignalCategories();
      await this.loadFavouriteSignalsOrFallbackToAll();
      this.initialised = true;
    }
  };

  private getSignalForMobile = async () => {
    await this.loadFavouriteSignalsOrFallbackToAll();
  };

  private loadFavouriteSignalsOrFallbackToAll = async () => {
    const favouriteSignals = await this.setFilter(ESignalFilter.FAVOURITE, false);

    if (favouriteSignals.length === 0) {
      this.context.selectedFilter = ESignalFilter.ALL;
      this.setFilter(ESignalFilter.ALL, false);
    }
  };

  public reset = () => {
    this.context = {
      ...this.context,
      ...defaultContext,
      selectedSignal: undefined,
      selectedSignalDetails: undefined,
      selectedCategories: this.context.originalSelectedCategories,
      loadingSymbolDetails: false
    };
    this.initialised = false;
  };

  public resetSignalAndSymbolDetails = () => {
    this.context.selectedSignalDetails = undefined;
    this.context.selectedSymbolDetails = undefined;
  };

  public setKeyword = (keyword: string) => {
    this.context.keyword = keyword;

    if (this.context.selectedFilter === ESignalFilter.FAVOURITE && keyword) {
      this.context.selectedFilter = ESignalFilter.ALL;
    }
  };

  public clearKeyword = () => {
    this.context.keyword = defaultContext.keyword;
  };

  public onSelectSignalCategory = (selectedCategory: ESymbolType, checked: boolean) => {
    const selectedCategories = this.context.selectedCategories;

    if (checked) {
      this.context.selectedCategories = selectedCategories.filter((category) => category !== selectedCategory);
    } else {
      this.context.selectedCategories = [...selectedCategories, selectedCategory];
    }
  };

  public onSelectCategories = (categories: ESymbolType[]) => {
    this.context.selectedCategories = categories;
  };

  public onSelectFilter = (filter: ESignalFilter) => {
    this.context.selectedFilter = filter;
    this.setFilter(filter);
  };

  public onSelectSignal = (signal: ISignal) => {
    this.context.selectedSignal = signal;

    const { id, symbol } = signal;

    this.fetchSignalDetails(id, symbol);

    return this.fetchSymbolDetails(symbol);
  };

  public onSelectSignalFromNotification = async (params: IOnSelectSignalFromNotificationParams) => {
    const { symbol, signalId } = params;
    const appType = configService.getAppType();

    if (appType === EAppType.PC) {
      this.initialised = true;

      this.context.selectedFilter = ESignalFilter.FAVOURITE;

      if (this.context.favourites.length === 0) {
        await this.fetchFavouriteSignals();
      }

      const favouriteSignals = this.context.favourites;

      const selectedSignal = favouriteSignals.find((signal) => signal.id === signalId && signal.symbol === symbol);

      if (selectedSignal) {
        return this.onSelectSignal(selectedSignal);
      } else {
        this.context.selectedSignal = undefined;

        return this.fetchSymbolAndSignalDetails(symbol, signalId);
      }
    } else {
      // for mobile, the Signal Screen will fetch symbol and signal details from useEffect call, just need to set signal to view
      this.setSignalToViewWithProps({ id: signalId, symbol });
      return Promise.resolve();
    }
  };

  public onRemoveSymbolWatch = (symbol: string) => {
    watchlistAPI.delete({
      symbol
    });

    this.context.favourites = this.context.favourites.filter((signal) => {
      return signal.symbol !== symbol;
    });
  };

  public updateAllSymbolListForWatch = async (symbol: string, watch: boolean) => {
    try {
      const { selectedSymbolDetails } = this.context;

      this.context.favourites = this.context.favourites.map((signal) => {
        if (signal.symbol === symbol) {
          return {
            ...signal,
            watch
          };
        }

        return signal;
      });

      this.context.signals = this.context.signals.map((signal) => {
        if (signal.symbol === symbol) {
          return {
            ...signal,
            watch
          };
        }

        return signal;
      });

      if (selectedSymbolDetails && selectedSymbolDetails.symbol === symbol) {
        this.context.selectedSymbolDetails = {
          ...selectedSymbolDetails,
          watch
        };
      }
    } catch (error) {}
  };

  public fetchFavouriteSignals = async (params: IFetchSignalsParams = {}) => {
    const { skipSelectDefault = true, loading = true } = params;
    const appType = configService.getAppType();

    try {
      this.context.loading = appType === EAppType.PC ? this.context.favourites.length === 0 : loading;

      const { result } = await signalsAPI.favourites();

      const sortedFavouriteSignals = result?.sort(this.sorter) || [];

      this.setDefaultFavouriteSignals(sortedFavouriteSignals);

      if (appType === EAppType.PC) {
        const selectDefault = authService.context.source === ELoginSource.WEB && !this.context.selectedSignal;

        if (!skipSelectDefault && selectDefault && sortedFavouriteSignals.length > 0) {
          const firstFavoriteSignal = sortedFavouriteSignals[0];

          this.onSelectSignal(firstFavoriteSignal);
        }
      }
      return sortedFavouriteSignals;
    } catch (error) {
      return [];
    } finally {
      this.context.loading = false;
    }
  };

  public fetchSignals = async (params?: IFetchSignalsParams) => {
    const { skipSelectDefault = true, loading = true } = params || {};
    const appType = configService.getAppType();

    try {
      this.context.loading = appType === EAppType.MOBILE ? loading : this.context.signals.length === 0;

      const { result } = await signalsAPI.all();

      const sortedAllSignals = result?.sort(this.sorter) || [];

      this.context.signals = sortedAllSignals;

      if (appType === EAppType.PC) {
        const selectDefault = authService.context.source === ELoginSource.WEB && !this.context.selectedSignal;

        if (!skipSelectDefault && selectDefault && sortedAllSignals && sortedAllSignals.length > 0) {
          const firstSignal = sortedAllSignals[0];

          this.onSelectSignal(firstSignal);
        }
      }

      return sortedAllSignals;
    } catch {
      return [];
    } finally {
      this.context.loading = false;
    }
  };

  public softRefresh = () => {
    const { selectedFilter, selectedSignal, selectedSymbolDetails } = this.context;

    switch (selectedFilter) {
      case ESignalFilter.ALL:
        this.fetchSignals();
        break;
      case ESignalFilter.FAVOURITE:
        this.fetchFavouriteSignals();
        break;
      default:
        break;
    }

    const loginSource = authService.context.source;

    if (loginSource === ELoginSource.WEB) {
      if (selectedSymbolDetails) {
        this.fetchSymbolDetails(selectedSymbolDetails.symbol, true);
      }

      if (selectedSignal) {
        this.fetchSignalDetails(selectedSignal.id, selectedSignal.symbol, true);
      }
    }
  };

  public resetSignalCategories = () => {
    this.context.selectedCategories = [...this.context.originalSelectedCategories];
  };

  public fetchSelectedSignalCategories = async (useLoader = true) => {
    try {
      this.context.loadingSignalCategories = useLoader;

      const { result } = await signalsAPI.categories();

      if (result && result.symbolTypes) {
        this.context.selectedCategories = result.symbolTypes;
        this.context.originalSelectedCategories = result.symbolTypes;
      }
    } catch (error) {
    } finally {
      this.context.loadingSignalCategories = false;
    }
  };

  public saveSignalCategoryChanges = async () => {
    try {
      this.context.savingSignalCategories = true;

      const uncheckedCategories = SIGNAL_CATEGORY_FILTERS.filter((category) => {
        return this.context.selectedCategories.findIndex((sortCategory) => sortCategory === category.key) === -1;
      }).map((category) => category.key);

      await signalsAPI.hide({
        symbolTypeList: uncheckedCategories
      });

      this.fetchSignals();

      this.context.originalSelectedCategories = [...this.context.selectedCategories];

      return {
        success: true,
        message: i18n.t('All changes are saved.')
      };
    } catch (err) {
      const { code, message } = err as IApiError;

      return {
        success: false,
        message: mapResponseCodeToMessage(code, message)
      };
    } finally {
      this.context.savingSignalCategories = false;
    }
  };

  private setDefaultFavouriteSignals = (favouriteSignals: ISignal[]) => {
    this.context.favourites = favouriteSignals.map((signal) => ({
      ...signal,
      watch: true
    }));

    return this.context.favourites;
  };

  private fetchSymbolDetails = async (symbol: string, softFetch = false) => {
    const appType = configService.getAppType();
    const loginSource = authService.context.source;

    try {
      this.context.loadingSymbolDetails = appType === EAppType.PC ? !softFetch : true;

      const { result } = await symbolDetailAPI({
        symbol
      });

      if (!result) {
        return;
      }

      const { symbol: id, decimal, description } = result;

      this.context.selectedSymbolDetails = result;

      chartService.onSelectSymbol(id, decimal, { description });

      if (loginSource === ELoginSource.WEB) {
        userPreferenceService.getLineDrawingUserPreference(symbol);
      }

      if (appType === EAppType.PC) {
        if (!softFetch) {
          newOrderService.init(result);
          orderService.setSymbolDetail(result);
        }
      }
    } catch (err) {
      if (appType === EAppType.PC) {
        const { code, message } = err as IApiError;

        throw new Error(mapResponseCodeToMessage(code, message));
      }
    } finally {
      this.context.loadingSymbolDetails = false;
    }
  };

  private fetchSignalDetails = async (id: string, symbol: string, softFetch = false) => {
    const appType = configService.getAppType();

    try {
      this.context.loadingSignalDetails = appType === EAppType.PC ? !softFetch : true;

      const { result } = await signalsDetailsAPI({
        id,
        symbol
      });

      if (result) {
        this.context.selectedSignalDetails = result;
      }
    } catch (err) {
      if (appType === EAppType.PC) {
        const { code, message } = err as IApiError;
        throw new Error(mapResponseCodeToMessage(code, message));
      }
    } finally {
      this.context.loadingSignalDetails = false;
    }
  };

  private fetchSymbolAndSignalDetails = (symbol: string, signalId: string) => {
    this.fetchSignalDetails(signalId, symbol);

    return this.fetchSymbolDetails(symbol);
  };

  private sorter = (a: ISignal, b: ISignal) => {
    if (a.isDisplay === false) {
      return 1;
    }

    if (b.isDisplay === false) {
      return -1;
    }

    if (a.publishedDate > b.publishedDate) {
      return -1;
    }

    if (a.publishedDate < b.publishedDate) {
      return 1;
    }

    return 0;
  };

  // +++++ For mobile only start +++++

  public setWatch = (symbol: string, watch: boolean) => {
    if (watch) {
      watchlistAPI.add({
        symbol
      });
    } else {
      watchlistAPI.delete({
        symbol
      });
    }

    const { selectedSymbolDetails } = this.context;

    if (selectedSymbolDetails) {
      this.context.selectedSymbolDetails = {
        ...selectedSymbolDetails,
        watch
      };
    }

    this.context.favourites = this.context.favourites.filter((signal) => {
      return signal.symbol !== symbol;
    });
  };

  public setSignalToView = (signal: ISignal) => {
    // Set the signal to be viewed so mobile signal screen will handle responsibility of loading
    this.context.signalToView = signal;
  };

  public setSignalToViewWithProps = ({ id, symbol }: ISetSignalToViewWithPropsParams) => {
    this.context.signalToView = {
      id,
      symbol
    };
  };

  public fetchDataForSignalToView = () => {
    const { signalToView } = this.context;
    if (signalToView) {
      const { symbol, id } = signalToView;
      this.fetchSymbolDetails(symbol);
      this.fetchSignalDetails(id, symbol);
    }
  };

  public clearSelectedSignal = () => {
    this.context.loadingSignalDetails = true;
    this.context.loadingSymbolDetails = true;
    this.context.selectedSymbolDetails = undefined;
    this.context.selectedSignalDetails = undefined;
  };

  public setFilter = async (filter: ESignalFilter, initialised = true) => {
    const params: IFetchSignalsParams = {
      skipSelectDefault: initialised
    };
    this.context.selectedFilter = filter;
    switch (filter) {
      case ESignalFilter.FAVOURITE:
        return await this.fetchFavouriteSignals(params);
      default:
        return await this.fetchSignals(params);
    }
  };

  public refreshAllSignals = () => {
    this.fetchSignals({ loading: false });
  };

  // ----- For mobile only end -----
}

export default new SignalsService();
